import { Quaternion, Vector3 } from 'three';
import type { PixelCamera } from '../../camera/PixelCamera';
import { stabBearing } from '../../characters/rangerLook';
import { put, QUARTER_TURN, yawQuat } from './batch';
import { type EventOf, type Fx, PRONE_HEIGHT, ROCKET_HEIGHT } from './context';
import type { Drawer } from './particles';

/* Muzzle flashes, the LAW's launch flash, and the knife's arc: the two-frame, self-lit effects. */

const MUZZLE_HEIGHT = 1.2;
const MUZZLE_AHEAD = 0.35;
const EMPLACEMENT_HEIGHT = 0.75;
const EMPLACEMENT_AHEAD = 1.3;
const FLASH_LIFE = 0.05;
const KNIFE_LIFE = 0.08;
/** Size of the muzzle star with flashes turned down. */
const SOFT_STAR = 0.6;

const X_AXIS = new Vector3(1, 0, 0);
const ROLL_45 = new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), QUARTER_TURN);

/** Draws the flash star and the knife bar. Stars are flat, so they are turned square-on to the camera. */
export class Flashes {
  private readonly faceCamera = new Quaternion();
  private readonly faceCameraRolled = new Quaternion();

  /** Once per frame, before particles are drawn: the camera's pitch is fixed but not known here. */
  aim(camera: PixelCamera): void {
    this.faceCamera.setFromAxisAngle(X_AXIS, -Math.atan2(camera.sinPitch, camera.cosPitch));
    this.faceCameraRolled.copy(this.faceCamera).multiply(ROLL_45);
  }

  /** Two frames: the full star, then a smaller one rolled 45 degrees. */
  readonly drawFlash: Drawer = (p, camera) => {
    const second = p.age / p.life >= 0.5;
    const s = p.size * (second ? 0.6 : 1);
    put(p.batch, camera, p.x, p.y, p.z, s, s, s, second ? this.faceCameraRolled : this.faceCamera);
  };

  /** Three stepped positions of a bar swung about the knifeman. */
  readonly drawSlash: Drawer = (p, camera) => {
    const swing = (Math.min(2, Math.floor((p.age / p.life) * 3)) - 1) * 0.85;
    put(p.batch, camera, p.x, p.y, p.z, p.size, p.size, p.size, yawQuat(p.yaw + swing));
  };
}

/** Star at the muzzle, a brief light, and one casing thrown clear. */
export function shot(fx: Fx, e: EventOf<'shot'>): void {
  const prone = e.fromPlayer && fx.world?.ranger.stance === 'crawl';
  const ground = fx.groundY(e.x, e.z);
  // A nest's gun sits low on its tripod, its muzzle well out in front of the gunner (kit/structures).
  const y = ground + (e.emplacement ? EMPLACEMENT_HEIGHT : prone ? PRONE_HEIGHT : MUZZLE_HEIGHT);
  const ahead = e.emplacement ? EMPLACEMENT_AHEAD : MUZZLE_AHEAD;
  const x = e.x + e.dirX * ahead;
  const z = e.z + e.dirZ * ahead;
  const star = (e.emplacement ? 1.7 : 1) * (fx.toggles.flashes ? 1 : SOFT_STAR);
  fx.particles.spawn('flash', fx.set.b.flash, x, y, z, star, FLASH_LIFE);
  fx.lights.flash(x, y + 0.3, z, fx.muzzleColor, e.emplacement ? 22 : 12, 7, 0.06, false);
  // The casing leaves to the shooter's right and a little backward.
  const side = fx.rng.range(1.2, 2.0);
  fx.particles.cube(fx.set.b.cubeBrass, e.x, y, e.z, 0.1, 0.7, -e.dirZ * side - e.dirX * 0.4, fx.rng.range(2.2, 3.2), e.dirX * side - e.dirZ * 0.4, ground);
}

/** The event has no heading, so the back-blast is a flash and a knot of smoke at the launcher. */
export function rocketLaunch(fx: Fx, e: EventOf<'rocket'>): void {
  const y = fx.groundY(e.x, e.z) + ROCKET_HEIGHT;
  const star = fx.toggles.flashes ? 1.3 : 1.3 * SOFT_STAR;
  fx.particles.spawn('flash', fx.set.b.flash, e.x, y, e.z, star, FLASH_LIFE);
  fx.lights.flash(e.x, y + 0.3, e.z, fx.muzzleColor, 26, 9, 0.1, false);
  for (let i = 0; i < 3; i++) {
    const p = fx.particles.puff(fx.set.b.smokeMid, e.x, y - 0.2, e.z, fx.rng.range(0.32, 0.48), fx.rng.range(0.5, 0.75), 0.8, i * 0.03);
    p.vx = fx.rng.range(-0.8, 0.8);
    p.vz = fx.rng.range(-0.8, 0.8);
  }
}

export function knife(fx: Fx, e: EventOf<'knife'>): void {
  // Only the ranger carries a knife, and the event sits 0.8 m toward the man it found (on the aim when it
  // found nobody): the swing goes from him toward that point, which at point-blank can be well off his aim.
  const ranger = fx.world?.ranger;
  const p = fx.particles.spawn('slash', fx.set.b.slash, e.x, fx.groundY(e.x, e.z) + 1.0, e.z, 1, KNIFE_LIFE);
  p.yaw = ranger ? stabBearing(ranger, e) : Math.PI;
}
